import React from 'react';

export type OmdAccentColor = 'green' | 'blue' | 'slate' | 'violet' | 'orange';
export type OmdPanelDensity = 'comfortable' | 'kompakt';
export type OmdPanelLayout = 'tabs' | 'fliessend';
export type OmdDocumentsLayout = 'raster' | 'liste';

export type OmdPanelSettings = {
  accentColor: OmdAccentColor;
  density: OmdPanelDensity;
  panelWidthPx: number;
  layout: OmdPanelLayout;
  documentsLayout: OmdDocumentsLayout;
  showEmptyFields: boolean;
};

export const DEFAULT_OMD_PANEL_SETTINGS: OmdPanelSettings = {
  accentColor: 'green',
  density: 'comfortable',
  panelWidthPx: 680,
  layout: 'tabs',
  documentsLayout: 'raster',
  showEmptyFields: false,
};

const STORAGE_KEY = 'omd-panel-settings';

export function loadOmdPanelSettings(): OmdPanelSettings {
  if (typeof window === 'undefined') return DEFAULT_OMD_PANEL_SETTINGS;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_OMD_PANEL_SETTINGS;
    const parsed = JSON.parse(raw) as Partial<OmdPanelSettings>;
    return {
      ...DEFAULT_OMD_PANEL_SETTINGS,
      ...parsed,
      panelWidthPx: clampWidth(parsed.panelWidthPx ?? DEFAULT_OMD_PANEL_SETTINGS.panelWidthPx),
    };
  } catch {
    return DEFAULT_OMD_PANEL_SETTINGS;
  }
}

export function saveOmdPanelSettings(settings: OmdPanelSettings): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
  } catch {
    /* ignore */
  }
}

function clampWidth(px: number): number {
  return Math.min(840, Math.max(560, Math.round(px)));
}

const ACCENT_CLASSES: Record<OmdAccentColor, { primary: string; primaryHover: string; soft: string; ring: string }> = {
  green: {
    primary: 'bg-green-500 hover:bg-green-600',
    primaryHover: 'text-green-600 hover:text-green-700',
    soft: 'bg-green-50 border-green-100 text-green-800',
    ring: 'focus-visible:ring-green-500/30',
  },
  blue: {
    primary: 'bg-pipedrive-blue hover:bg-blue-700',
    primaryHover: 'text-pipedrive-blue hover:text-blue-700',
    soft: 'bg-blue-50 border-blue-100 text-blue-800',
    ring: 'focus-visible:ring-pipedrive-blue/30',
  },
  slate: {
    primary: 'bg-slate-700 hover:bg-slate-800',
    primaryHover: 'text-app-text-muted hover:text-app-text',
    soft: 'bg-app-surface-muted border-app-border text-app-text',
    ring: 'focus-visible:ring-slate-400/30',
  },
  violet: {
    primary: 'bg-violet-600 hover:bg-violet-700',
    primaryHover: 'text-violet-600 hover:text-violet-700',
    soft: 'bg-violet-50 border-violet-100 text-violet-800',
    ring: 'focus-visible:ring-violet-500/30',
  },
  orange: {
    primary: 'bg-orange-500 hover:bg-orange-600',
    primaryHover: 'text-orange-600 hover:text-orange-700',
    soft: 'bg-orange-50 border-orange-100 text-orange-800',
    ring: 'focus-visible:ring-orange-500/30',
  },
};

export function getAccentClasses(accent: OmdAccentColor) {
  return ACCENT_CLASSES[accent];
}

export function getDensityClasses(density: OmdPanelDensity) {
  return density === 'kompakt'
    ? { section: 'py-2 px-3', text: 'text-xs', heading: 'text-sm', gap: 'gap-2' }
    : { section: 'py-3 px-4', text: 'text-sm', heading: 'text-base', gap: 'gap-3' };
}

export function useOmdPanelSettings(): [OmdPanelSettings, (patch: Partial<OmdPanelSettings>) => void] {
  const [settings, setSettings] = React.useState<OmdPanelSettings>(() => loadOmdPanelSettings());

  const update = React.useCallback((patch: Partial<OmdPanelSettings>) => {
    setSettings((prev) => {
      const next = {
        ...prev,
        ...patch,
        panelWidthPx: patch.panelWidthPx != null ? clampWidth(patch.panelWidthPx) : prev.panelWidthPx,
      };
      saveOmdPanelSettings(next);
      return next;
    });
  }, []);

  return [settings, update];
}
